import { Tabs, Typography } from 'antd';
import React from 'react';
import { useSelector } from 'react-redux';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { t } from 'ttag';

import HTTP404 from 'common/components/ErrorRoutes/HTTP404';
import Loading from 'common/components/Loading';
import { getName } from 'common/state/organization';

import { useContracts } from '@domains/Organization/DataUsage/PeopleCredit/queries';
import useOrganizationTabs, {
  TAB_ADMIN,
  TAB_APPS,
  TAB_EVENTS,
  TAB_API_KEYS,
  TAB_ORGANIZERS,
  TAB_SSO,
  TAB_EMAIL_DOMAIN,
  TAB_PAYMENT_GATEWAYS,
  TAB_PEOPLE_CREDIT,
  TAB_VIDEO_USAGE,
  TAB_CUSTOM_DOMAIN,
  VALID_TABS,
} from '@hooks/useOrganizationTabs';

import Admin from './Admin';
import APIKeys from './APIKeys';
import Apps from './App';
import CustomDomain from './CustomDomain';
import PeopleCredits from './DataUsage/PeopleCredit';
import VideoUsage from './DataUsage/Video';
import EmailDomain from './EmailDomain';
import Events from './Event';
import Organizers from './Organizer';
import PaymentGateways from './PaymentGateway';
import SSO from './SSO';

const { Title: AntTitle, Text } = Typography;

const Title = styled(AntTitle)`
  margin-left: 45px;
  margin-top: 40px;
  padding-bottom: 40px;
  && {
    font-weight: 400;
  }
`;

const Details = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { organizationId, tab } = params;
  const name = useSelector(getName);

  const { data: contracts } = useContracts(organizationId);
  const { tabs: baseTabs, isLoading } = useOrganizationTabs(organizationId);

  if (!VALID_TABS.includes(tab)) {
    return (
      <HTTP404
        scoped
        ctaText={t`Return to Dashboard`}
        ctaHref={generatePath('organization/:organizationId', { organizationId })}
      />
    );
  }

  if (isLoading) {
    return <Loading />;
  }

  // Map tab children based on tab key
  const getTabChildren = tabKey => {
    switch (tabKey) {
      case TAB_EVENTS:
        return <Events active={tab === TAB_EVENTS} />;
      case TAB_ORGANIZERS:
        return <Organizers />;
      case TAB_PEOPLE_CREDIT:
        return <PeopleCredits contracts={contracts} />;
      case TAB_VIDEO_USAGE:
        return <VideoUsage />;
      case TAB_API_KEYS:
        return <APIKeys />;
      case TAB_SSO:
        return <SSO active={tab === TAB_SSO} />;
      case TAB_EMAIL_DOMAIN:
        return <EmailDomain active={tab === TAB_EMAIL_DOMAIN} />;
      case TAB_PAYMENT_GATEWAYS:
        return <PaymentGateways active={tab === TAB_PAYMENT_GATEWAYS} />;
      case TAB_CUSTOM_DOMAIN:
        return <CustomDomain />;
      case TAB_APPS:
        return <Apps />;
      case TAB_ADMIN:
        return <Admin />;
      default:
        return null;
    }
  };

  // Build final tabs with children and styling
  const tabs = baseTabs.map(tabConfig => ({
    ...tabConfig,
    label: <Text style={{ color: tabConfig.color }}>{tabConfig.label}</Text>,
    children: getTabChildren(tabConfig.key),
  }));

  if (!tabs.some(({ key }) => key === tab)) {
    window.location.replace('/forbidden');
    return null;
  }

  return (
    <>
      <Title>{name}</Title>
      <Tabs
        type="card"
        activeKey={tab}
        style={{ overflow: 'visible' }}
        tabBarStyle={{ marginBottom: 0, paddingLeft: 45 }}
        onChange={newTab => {
          navigate(
            generatePath('/organization/:organizationId/:tab', {
              organizationId: params.organizationId,
              tab: newTab,
            }),
          );
        }}
        items={tabs}
        destroyOnHidden
      />
    </>
  );
};

export default Details;
